import { ActionConfirmModal } from '@/src/components'
import { Menu, MenuButton, MenuItem, MenuList } from '@/src/components/ui/Menu'
import { useSessionStore } from '@/src/hooks/use_session_store'
import { Box, Portal, Text, Tooltip, useDisclosure } from '@chakra-ui/react'
import type { MouseEventHandler, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { BsThreeDots } from 'react-icons/bs'
import { HiOutlineTrash } from 'react-icons/hi'
import { SlPencil } from 'react-icons/sl'

// Update the MenuOption type to include an optional component
export interface MenuOption {
  name: string
  icon?: ReactElement
  onClick?: () => void
  color?: string
  hoverBg?: string
  component?: ReactElement
  disabled?: boolean
  tooltipLabel?: string
  type?: 'standard' | 'danger'
}

export interface DeleteConfirmModalProps {
  title?: string
  acceptLabel?: string
  cancelLabel?: string
  body?: ReactElement
  isOpen: boolean
  onClose: () => void
  onAccept: () => void
  isLoading?: boolean
}

export const DeleteConfirmModal = ({
  title,
  acceptLabel,
  cancelLabel,
  body,
  isOpen,
  onClose,
  onAccept,
  isLoading,
}: DeleteConfirmModalProps) => {
  const { t } = useTranslation()

  return (
    <ActionConfirmModal
      title={title ?? t('delete_confirm_modal.title')}
      acceptLabel={acceptLabel ?? t('delete_confirm_modal.accept_label')}
      cancelLabel={cancelLabel ?? t('delete_confirm_modal.cancel_label')}
      actionButtonColor='red'
      body={
        body ?? (
          <Text fontSize='14px' color='gray.600' fontWeight={500}>
            {t('delete_confirm_modal.body')}
          </Text>
        )
      }
      isOpen={isOpen}
      onClose={onClose}
      onAccept={onAccept}
      isLoading={isLoading}
    />
  )
}

interface IEditMenuProps {
  // itemId: string
  onButtonClick?: MouseEventHandler<HTMLButtonElement>
  onEdit?: () => void
  onDelete?: () => void
  onConfirmModalDelete?: () => Promise<void>
  onMenuClose?: () => void
  deleteConfirmModal?: {
    title?: string
    acceptLabel?: string
    cancelLabel?: string
    body?: ReactElement
  }
  size?: number
  renderOnPortal?: boolean
  zIndex?: number
  disableEdit?: boolean
  disableDelete?: boolean
  deleteLabel?: string
  editLabel?: string
  hideDeleteModal?: boolean
  customOptions?: MenuOption[]
  isLoading?: boolean
  isDisabled?: boolean
}

export const EditMenu = ({
  onButtonClick,
  onEdit = () => {},
  onDelete = () => {},
  onConfirmModalDelete,
  onMenuClose,
  deleteConfirmModal,
  size,
  renderOnPortal,
  zIndex,
  isDisabled,
  disableEdit,
  disableDelete,
  deleteLabel,
  editLabel,
  hideDeleteModal,
  customOptions = [],
  isLoading,
}: IEditMenuProps) => {
  const deleteModal = useDisclosure()
  const { user } = useSessionStore()
  const { t } = useTranslation()

  const defaultMenuOptions: MenuOption[] = [
    {
      name: editLabel ?? t('common.edit'),
      icon: <SlPencil size='16px' />,
      onClick: () => onEdit(),
      disabled: user?.readonly,
      tooltipLabel: user?.readonly ? t('readonly_action_disabled') : undefined,
    },
    {
      name: deleteLabel ?? t('common.delete'),
      icon: <HiOutlineTrash size='18px' />,
      type: 'danger',
      onClick: () => {
        if (hideDeleteModal) {
          onDelete()
        } else {
          onConfirmModalDelete && onDelete()
          deleteModal.onOpen()
        }
      },
      disabled: user?.readonly,
      tooltipLabel: user?.readonly ? t('readonly_action_disabled') : undefined,
    },
  ]

  const filteredMenuOptions = [
    ...defaultMenuOptions.filter(option => option.name === t('common.edit') && !disableEdit),
    ...customOptions,
    ...defaultMenuOptions.filter(option => option.name === t('common.delete') && !disableDelete),
  ]

  const menuButton = (
    <MenuButton
      data-testid={`menu-button`}
      zIndex={zIndex}
      onClick={e => {
        e.stopPropagation()
        onButtonClick?.(e)
      }}
      width='fit-content'
      isDisabled={isDisabled}
    >
      <BsThreeDots size={size} />
    </MenuButton>
  )

  const menuList = (
    <MenuList position='relative' zIndex={200}>
      {filteredMenuOptions.map(option => {
        if (option.component) {
          return (
            <Box key={`menu-option-${option.name}`} onClick={option.onClick}>
              {option.component}
            </Box>
          )
        }

        return (
          <Tooltip
            key={`menu-option-${option.name}`}
            label={option.tooltipLabel}
            bg='white'
            color='gray.800'
            fontWeight='normal'
            p='4'
            borderRadius='base'
            hasArrow
            placement='bottom-start'
          >
            <MenuItem
              class={option.type === 'danger' ? 'danger' : 'standard'}
              isDisabled={option.disabled}
              data-testid={`menu-item-${option.name}`}
              alignItems={'center'}
              onClick={e => {
                e.stopPropagation()
                option.onClick?.()
              }}
            >
              {option.icon && <Box marginRight={2}>{option.icon}</Box>}
              {option.name}
            </MenuItem>
          </Tooltip>
        )
      })}
    </MenuList>
  )

  return (
    <>
      <DeleteConfirmModal
        title={deleteConfirmModal?.title}
        acceptLabel={deleteConfirmModal?.acceptLabel}
        cancelLabel={deleteConfirmModal?.cancelLabel}
        body={deleteConfirmModal?.body}
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        onAccept={async () => {
          if (onConfirmModalDelete) {
            await onConfirmModalDelete()
          } else {
            onDelete()
          }
          deleteModal.onClose()
        }}
        isLoading={isLoading}
      />

      <Menu onClose={onMenuClose}>
        {menuButton}
        {renderOnPortal ? <Portal>{menuList}</Portal> : menuList}
      </Menu>
    </>
  )
}
